import React, { useEffect, useState } from 'react';
import { Control, Controller, UseFormWatch, UseFormSetValue, FieldErrors } from 'react-hook-form';
import { CollaboratorFormContext } from "@/features/Collaborators/Edit/Contexts/CollaboratorFormContext";
import { collaboratorSchemaHelpers } from '@/features/Collaborators/Models/collaborator';
import { FileUploadInput, RequiredIndicator, GooglePlacesControlledInput } from '@/components/shared';
import type { ParsedAddress } from '@/components/shared';
import { useFileUpload } from '@/hooks/form-handlers/useFileUpload';
import { CollaboratorFormData } from '@/features/Collaborators/Edit/Types';

export interface AddressFieldsetProps {
    context: CollaboratorFormContext;
    collaboratorId: string;
    control: Control<any>;
    setValue: UseFormSetValue<any>;
    watch: UseFormWatch<any>;
    errors?: FieldErrors<any>;
    isSubmitting?: boolean;
    layout?: 'compact' | 'full';
    proofUpload?: AddressProofUpload;
}

export interface AddressProofUpload {
    buildPath: (params: { file: File; id?: string; pathPrefix: string }) => string;
    onUploaded: (attachment: { name: string; url: string; path: string; contentType: string; size: number }) => void;
}

const AddressFieldset: React.FC<AddressFieldsetProps> = ({
    context,
    collaboratorId,
    control,
    setValue,
    watch,
    errors = {},
    isSubmitting = false,
    layout = 'full',
    proofUpload
}) => {
    // Estado local para os dados do formulário (necessário para o useFileUpload)
    const [formData, setFormData] = useState<CollaboratorFormData>({} as CollaboratorFormData);

    // File upload hook
    const { uploadStates, handleFileChange } = useFileUpload(setFormData);

    // Sincronizar mudanças do React Hook Form com o estado local
    useEffect(() => {
        const subscription = watch((value) => {
            // ✅ Usar setTimeout para evitar setState durante render
            setTimeout(() => {
                setFormData(value as CollaboratorFormData);
            }, 0);
        });
        return () => subscription.unsubscribe();
    }, [watch]);

    // Assistir mudanças no checkbox e endereço principal
    const isMailingSameAsMain = watch('isMailingSameAsMain') || false;
    const mainAddress = watch('addresses.0') || {};

    // Obter valores específicos do React Hook Form para o upload
    const watchedProofOfAddressUrl = watch('addresses.0.proofOfAddressUrl');
    const watchedProofOfAddressFile = watch('addresses.0.proofOfAddressFile');

    // URLs para os campos de upload (prioriza dados do formulário)
    const proofOfAddressUrl = watchedProofOfAddressUrl || formData.addresses?.[0]?.proofOfAddressUrl;

    // useEffect para atualizar URL quando mudar
    useEffect(() => {
        if (proofOfAddressUrl) {
            setValue('addresses.0.proofOfAddressUrl', proofOfAddressUrl);
        }
    }, [proofOfAddressUrl, setValue]);

    // Estado de upload específico para comprovante de endereço
    const addressProofState = uploadStates['addresses.0.proofOfAddressFile'];


    // Helper local para extrair mensagem de erro simples
    const getErrorMessage = (errorPath: string) => {
        const pathParts = errorPath.split('.');
        let current: any = errors;
        for (const part of pathParts) {
            if (current && typeof current === 'object') {
                if (!isNaN(Number(part))) {
                    current = current[Number(part)];
                } else {
                    current = current[part];
                }
            } else {
                return null;
            }
        }
        return current?.message || null;
    };

    // Verificar se os campos devem ser mostrados no contexto atual
    const shouldShowAddressSection = collaboratorSchemaHelpers.shouldShowField('addresses.0.fullAddress', context);

    // useEffect para copiar endereço principal para correspondência quando checkbox marcado
    useEffect(() => {
        if (isMailingSameAsMain && mainAddress) {
            setValue('addresses.1.fullAddress', mainAddress.fullAddress || '');
            setValue('addresses.1.street', mainAddress.street || '');
            setValue('addresses.1.number', mainAddress.number || '');
            setValue('addresses.1.neighborhood', mainAddress.neighborhood || '');
            setValue('addresses.1.city', mainAddress.city || '');
            setValue('addresses.1.state', mainAddress.state || '');
            setValue('addresses.1.country', mainAddress.country || '');
            setValue('addresses.1.zipCode', mainAddress.zipCode || '');
            setValue('addresses.1.latitude', mainAddress.latitude || 0);
            setValue('addresses.1.longitude', mainAddress.longitude || 0);
            setValue('addresses.1.complement', mainAddress.complement || '');
            setValue('addresses.1.type', 'mailing');
        }
    }, [isMailingSameAsMain, mainAddress, setValue]);

    if (!shouldShowAddressSection) {
        return null;
    }

    return (
        <div className="space-y-6" data-testid="address-fieldset">
            {/* Endereço Principal */}
            <fieldset className="border p-4 rounded-md" data-testid="fieldset-main-address">
                <legend className="text-sm font-medium px-1 text-gray-400">Endereço Principal</legend>
                <div className="space-y-4 pt-2">
                    <div>
                        <label className="block text-sm font-medium text-gray-700">
                            Endereço
                            {collaboratorSchemaHelpers.isFieldRequired('addresses.0.fullAddress', context) && <RequiredIndicator />}
                        </label>
                        <Controller
                            name="addresses.0.fullAddress"
                            control={control}
                            render={({ field }) => (
                                <GooglePlacesControlledInput
                                    name="addresses.0.fullAddress"
                                    control={control}
                                    onSelectAddress={(addr: ParsedAddress) => {
                                        field.onChange(addr.fullAddress);
                                        // Atualizar outros campos do endereço via setValue
                                        setValue('addresses.0.street', addr.street);
                                        setValue('addresses.0.number', addr.number);
                                        setValue('addresses.0.neighborhood', addr.neighborhood);
                                        setValue('addresses.0.city', addr.city);
                                        setValue('addresses.0.state', addr.state);
                                        setValue('addresses.0.country', addr.country);
                                        setValue('addresses.0.zipCode', addr.zipCode);
                                        setValue('addresses.0.latitude', addr.latitude);
                                        setValue('addresses.0.longitude', addr.longitude);
                                        setValue('addresses.0.type', 'main');
                                    }}
                                    disabled={isSubmitting}
                                    data-testid="main-address-autocomplete"
                                />
                            )}
                        />
                        {getErrorMessage('addresses.0.fullAddress') && (
                            <p className="mt-2 text-sm text-red-600" data-testid="error-main-address">
                                {getErrorMessage('addresses.0.fullAddress')}
                            </p>
                        )}
                    </div>

                    <div className={`grid gap-4 ${layout === 'compact' ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2'}`}>
                        <div>
                            <label htmlFor="mainAddressNumber" className="block text-sm font-medium text-gray-700">
                                Número
                                {collaboratorSchemaHelpers.isFieldRequired('addresses.0.number', context) && <RequiredIndicator />}
                            </label>
                            <Controller
                                name="addresses.0.number"
                                control={control}
                                render={({ field }) => (
                                    <input
                                        {...field}
                                        type="text"
                                        id="mainAddressNumber"
                                        className="input-primary w-full mt-1"
                                        disabled={isSubmitting}
                                        data-testid="main-address-number-input"
                                    />
                                )}
                            />
                            {getErrorMessage('addresses.0.number') && (
                                <p className="mt-2 text-sm text-red-600" data-testid="error-main-number">
                                    {getErrorMessage('addresses.0.number')}
                                </p>
                            )}
                        </div>

                        <div>
                            <label htmlFor="mainAddressComplement" className="block text-sm font-medium text-gray-700">
                                Complemento
                            </label>
                            <Controller
                                name="addresses.0.complement"
                                control={control}
                                render={({ field }) => (
                                    <input
                                        {...field}
                                        type="text"
                                        id="mainAddressComplement"
                                        className="input-primary w-full mt-1"
                                        disabled={isSubmitting}
                                        data-testid="main-address-complement-input"
                                    />
                                )}
                            />
                        </div>
                    </div>

                    <Controller
                        name="addresses.0.proofOfAddressFile"
                        control={control}
                        render={({ field, fieldState }) => (
                            <FileUploadInput
                                name="proofOfAddressFile"
                                label="Comprovante de Endereço"
                                fileUrl={watchedProofOfAddressUrl}
                                fileName={watchedProofOfAddressFile?.name}
                                isSubmitting={isSubmitting}
                                onChange={async (e: React.ChangeEvent<HTMLInputElement>) => {
                                    const file = e.target.files?.[0];
                                    if (!file) return;

                                    // Primeiro registrar o arquivo no React Hook Form
                                    field.onChange(file);

                                    // Depois fazer o upload
                                    await handleFileChange(e, {
                                        fieldName: 'addresses.0.proofOfAddressFile',
                                        urlFieldName: 'addresses.0.proofOfAddressUrl',
                                        pathPrefix: 'address_proofs',
                                        id: collaboratorId,
                                        ...(proofUpload && {
                                            buildPath: proofUpload.buildPath,
                                            onUploadComplete: ({ downloadURL, path, file }) =>
                                                proofUpload.onUploaded({
                                                    name: file.name,
                                                    url: downloadURL,
                                                    path,
                                                    contentType: file.type,
                                                    size: file.size,
                                                }),
                                        }),
                                    });
                                }}
                                progress={addressProofState?.progress}
                                status={addressProofState?.status}
                                error={fieldState.error?.message || (addressProofState?.error as string | undefined)}
                                isRequired={context === 'PROFILE_UPDATE'} // Obrigatório no PROFILE_UPDATE via validação "arquivo OU URL"
                                data-testid="main-address-proof-upload"
                            />
                        )}
                    />

                    {/* Controller oculto para garantir que a URL seja persistida */}
                    <Controller
                        name="addresses.0.proofOfAddressUrl"
                        control={control}
                        render={({ field }) => (
                            <input type="hidden" {...field} value={field.value || ''} />
                        )}
                    />
                </div>
            </fieldset>

            {/* Endereço de Correspondência */}
            <fieldset className="border p-4 rounded-md" data-testid="fieldset-mailing-address">
                <legend className="text-sm font-medium px-1 text-gray-400">Endereço de Correspondência</legend>
                <div className="pt-2">
                    <div className="flex items-center">
                        <Controller
                            name="isMailingSameAsMain"
                            control={control}
                            render={({ field }) => (
                                <input
                                    {...field}
                                    id="sameAsMain"
                                    type="checkbox"
                                    checked={field.value || false}
                                    onChange={(e) => field.onChange(e.target.checked)}
                                    className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                                    disabled={isSubmitting}
                                    data-testid="same-address-checkbox"
                                />
                            )}
                        />
                        <label htmlFor="sameAsMain" className="ml-2 block text-sm text-gray-900">
                            O endereço de correspondência é o mesmo que o principal.
                        </label>
                    </div>

                    {!isMailingSameAsMain && (
                        <div className="mt-4 space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700">
                                    Endereço
                                    {(collaboratorSchemaHelpers.isFieldRequired('addresses.1.fullAddress', context) && !isMailingSameAsMain) && <RequiredIndicator />}
                                </label>
                                <Controller
                                    name="addresses.1.fullAddress"
                                    control={control}
                                    render={({ field }) => (
                                        <GooglePlacesControlledInput
                                            name="addresses.1.fullAddress"
                                            control={control}
                                            onSelectAddress={(addr: ParsedAddress) => {
                                                field.onChange(addr.fullAddress);
                                                // Atualizar outros campos do endereço via setValue
                                                setValue('addresses.1.street', addr.street);
                                                setValue('addresses.1.number', addr.number);
                                                setValue('addresses.1.neighborhood', addr.neighborhood);
                                                setValue('addresses.1.city', addr.city);
                                                setValue('addresses.1.state', addr.state);
                                                setValue('addresses.1.country', addr.country);
                                                setValue('addresses.1.zipCode', addr.zipCode);
                                                setValue('addresses.1.latitude', addr.latitude);
                                                setValue('addresses.1.longitude', addr.longitude);
                                                setValue('addresses.1.type', 'mailing');
                                            }}
                                            disabled={isSubmitting}
                                            data-testid="mailing-address-autocomplete"
                                        />
                                    )}
                                />
                                {getErrorMessage('addresses.1.fullAddress') && (
                                    <p className="mt-2 text-sm text-red-600" data-testid="error-mailing-address">
                                        {getErrorMessage('addresses.1.fullAddress')}
                                    </p>
                                )}
                            </div>

                            <div className={`grid gap-4 ${layout === 'compact' ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2'}`}>
                                <div>
                                    <label htmlFor="mailingAddressNumber" className="block text-sm font-medium text-gray-700">
                                        Número
                                        {(collaboratorSchemaHelpers.isFieldRequired('addresses.1.number', context) && !isMailingSameAsMain) && <RequiredIndicator />}
                                    </label>
                                    <Controller
                                        name="addresses.1.number"
                                        control={control}
                                        render={({ field }) => (
                                            <input
                                                {...field}
                                                type="text"
                                                id="mailingAddressNumber"
                                                className="input-primary w-full mt-1"
                                                disabled={isSubmitting}
                                                data-testid="mailing-address-number-input"
                                            />
                                        )}
                                    />
                                    {getErrorMessage('addresses.1.number') && (
                                        <p className="mt-2 text-sm text-red-600" data-testid="error-mailing-number">
                                            {getErrorMessage('addresses.1.number')}
                                        </p>
                                    )}
                                </div>

                                <div>
                                    <label htmlFor="mailingAddressComplement" className="block text-sm font-medium text-gray-700">
                                        Complemento
                                    </label>
                                    <Controller
                                        name="addresses.1.complement"
                                        control={control}
                                        render={({ field }) => (
                                            <input
                                                {...field}
                                                type="text"
                                                id="mailingAddressComplement"
                                                className="input-primary w-full mt-1"
                                                disabled={isSubmitting}
                                                data-testid="mailing-address-complement-input"
                                            />
                                        )}
                                    />
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </fieldset>
        </div>
    );
};

export default AddressFieldset;
